import { GROUPS, OWNABLE_SQUARES, RESORT_SQUARES, SIDES, groupOf, valueOf } from './board';
import type { Effect, GameState, Player, PlayerId } from './types';
import { EngineError } from './types';

export function getPlayer(s: GameState, id: PlayerId): Player {
  const p = s.players.find((x) => x.id === id);
  if (!p) throw new EngineError('unknown_player', `Jogador desconhecido: ${id}`);
  return p;
}

export function currentPlayer(s: GameState): Player {
  return s.players[s.current]!;
}

/** Jogadores ainda na partida (quem quebrou sai). */
export function activePlayers(s: GameState): Player[] {
  return s.players.filter((p) => !p.eliminated);
}

export function ownerOf(s: GameState, index: number): PlayerId | null {
  return s.props[index]?.owner ?? null;
}

export function ownedBy(s: GameState, player: PlayerId): number[] {
  return OWNABLE_SQUARES.filter((i) => s.props[i]?.owner === player);
}

/** O jogador é dono de todas as casas do grupo desta casa? */
export function ownsFullGroup(s: GameState, player: PlayerId, index: number): boolean {
  const g = groupOf(index);
  return g.length > 0 && g.every((i) => s.props[i]?.owner === player);
}

export function completeGroupsOf(s: GameState, player: PlayerId): number[] {
  return Object.keys(GROUPS)
    .map(Number)
    .filter((g) => GROUPS[g]!.every((i) => s.props[i]?.owner === player));
}

/** Lados do tabuleiro em que o jogador tem todas as casas compráveis. */
export function completeSidesOf(s: GameState, player: PlayerId): number[] {
  return SIDES.map((side, k) => (side.every((i) => s.props[i]?.owner === player) ? k : -1)).filter((k) => k >= 0);
}

export function resortCount(s: GameState, player: PlayerId): number {
  return RESORT_SQUARES.filter((i) => s.props[i]?.owner === player).length;
}

/** Valor investido em todas as propriedades do jogador. */
export function propertyValue(s: GameState, player: PlayerId): number {
  return ownedBy(s, player).reduce((t, i) => t + valueOf(i, s.props[i]!.level), 0);
}

/** Patrimônio: dinheiro + valor investido nas propriedades. */
export function worth(s: GameState, player: PlayerId): number {
  return getPlayer(s, player).cash + propertyValue(s, player);
}

/** Jogadores do mais rico para o mais pobre (quem quebrou fica no fim; empate: ordem de assento). */
export function rankByWorth(s: GameState): { player: PlayerId; worth: number }[] {
  return s.players
    .map((p) => ({ player: p.id, worth: p.eliminated ? 0 : worth(s, p.id), seat: p.seat, out: p.eliminated }))
    .sort((a, b) => Number(a.out) - Number(b.out) || b.worth - a.worth || a.seat - b.seat)
    .map(({ player, worth: w }) => ({ player, worth: w }));
}

export function richest(s: GameState, among?: PlayerId[]): PlayerId {
  const r = rankByWorth(s).filter((x) => !getPlayer(s, x.player).eliminated && (!among || among.includes(x.player)));
  return r[0]!.player;
}

export function poorest(s: GameState, among?: PlayerId[]): PlayerId {
  const r = rankByWorth(s).filter((x) => !getPlayer(s, x.player).eliminated && (!among || among.includes(x.player)));
  return r[r.length - 1]!.player;
}

export function effectActive(s: GameState, e: Effect): boolean {
  return !('untilTurn' in e) || s.turnCounter < e.untilTurn;
}

export function hasEffect(s: GameState, predicate: (e: Effect) => boolean): boolean {
  return s.effects.some((e) => effectActive(s, e) && predicate(e));
}

/** Quantas jogadas formam uma rodada (todos que ainda estão na partida jogam uma vez). */
export function turnsPerRound(s: GameState): number {
  return Math.max(1, activePlayers(s).length);
}

export function otherPlayers(s: GameState, player: PlayerId): PlayerId[] {
  return activePlayers(s)
    .filter((p) => p.id !== player)
    .map((p) => p.id);
}
